import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Users, Plus, Edit2, Trash2, KeyRound, PhoneCall } from 'lucide-react';

export const GestioneUtenti = () => {
  const { isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [extensions, setExtensions] = useState([]);
  const [form, setForm] = useState({ nome: '', cognome: '', email: '', password: '', ruolo: 'dipendente', interno_voip: '' });

  useEffect(() => { fetchUsers(); fetchExtensions(); }, []);

  const fetchUsers = async () => {
    try { const r = await api.get('/users'); setUsers(r.data); } catch {}
    finally { setLoading(false); }
  };

  const fetchExtensions = async () => {
    try { const r = await api.get('/centralino/extensions'); setExtensions(r.data); } catch {}
  };

  const resetForm = () => setForm({ nome: '', cognome: '', email: '', password: '', ruolo: 'dipendente', interno_voip: '' });

  const openAdd = () => { resetForm(); setEditUser(null); setDialogOpen(true); };
  const openEdit = (u) => {
    setEditUser(u);
    setForm({ nome: u.nome, cognome: u.cognome, email: u.email, password: '', ruolo: u.ruolo, interno_voip: u.interno_voip || '' });
    setDialogOpen(true);
  };

  const openResetPassword = (u) => {
    setResetUser(u);
    setNewPassword('');
    setResetDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.nome || !form.cognome || !form.email) { toast.error('Compila tutti i campi'); return; }
    try {
      if (editUser) {
        const params = new URLSearchParams();
        if (form.nome) params.set('nome', form.nome);
        if (form.cognome) params.set('cognome', form.cognome);
        if (form.email) params.set('email', form.email);
        if (form.ruolo) params.set('ruolo', form.ruolo);
        if (form.password) params.set('password', form.password);
        await api.patch(`/users/${editUser.id}?${params.toString()}`);
        // Save VoIP extension separately
        if (form.interno_voip !== (editUser.interno_voip || '')) {
          await api.put(`/centralino/assign-extension?user_id=${editUser.id}&extension=${form.interno_voip}`);
        }
        toast.success('Utente aggiornato');
      } else {
        if (!form.password) { toast.error('Inserisci una password'); return; }
        const res = await api.post('/users/create', form);
        // Assign extension if set
        if (form.interno_voip && res.data?.id) {
          await api.put(`/centralino/assign-extension?user_id=${res.data.id}&extension=${form.interno_voip}`);
        }
        toast.success('Utente creato');
      }
      setDialogOpen(false);
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('La password deve avere almeno 6 caratteri');
      return;
    }
    try {
      await api.post(`/users/${resetUser.id}/reset-password?new_password=${encodeURIComponent(newPassword)}`);
      toast.success(`Password resettata per ${resetUser.nome} ${resetUser.cognome}`);
      setResetDialogOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel reset password');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Eliminare questo utente?')) return;
    try { await api.delete(`/users/${userId}`); toast.success('Utente eliminato'); fetchUsers(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  if (!isAdmin) return <div className="p-8 text-center text-slate-500">Accesso riservato agli amministratori</div>;

  return (
    <div className="p-5 md:p-7 min-h-screen" data-testid="gestione-utenti-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Gestione Utenti</h1>
          <p className="text-sm text-slate-500 mt-1">{users.length} utenti registrati</p>
        </div>
        <Button onClick={openAdd} data-testid="add-user-btn">
          <Plus size={16} className="mr-2" /> Nuovo Utente
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="grid grid-cols-[1fr_1fr_100px_80px_100px_120px] gap-4 px-5 py-3 bg-slate-50 border-b text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
          <span>Nome</span><span>Email</span><span>Ruolo</span><span>Interno</span><span>Stato</span><span></span>
        </div>
        {users.map(u => {
          const ext = extensions.find(e => e.internal_number === u.interno_voip);
          return (
          <div key={u.id} className="grid grid-cols-[1fr_1fr_100px_80px_100px_120px] gap-4 px-5 py-3.5 border-b border-slate-100 items-center hover:bg-slate-50 transition-colors" data-testid={`user-row-${u.id}`}>
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-9 w-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 font-semibold text-xs flex-shrink-0">
                {u.nome?.[0]}{u.cognome?.[0]}
              </div>
              <span className="text-sm font-medium text-slate-800 truncate">{u.nome} {u.cognome}</span>
            </div>
            <span className="text-sm text-slate-500 truncate">{u.email}</span>
            <div>
              <Badge variant="outline" className={`text-[10px] ${u.ruolo === 'super_admin' ? 'bg-amber-50 text-amber-700 border-amber-200' : u.ruolo === 'collaboratore' ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>
                {u.ruolo === 'super_admin' ? 'Admin' : u.ruolo === 'collaboratore' ? 'Collaboratore' : 'Dipendente'}
              </Badge>
            </div>
            <div>
              {u.interno_voip ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-[11px] font-medium" data-testid={`ext-${u.id}`}>
                  <PhoneCall size={10} /> {u.interno_voip}
                </span>
              ) : (
                <span className="text-[10px] text-slate-300">—</span>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <div className={`h-2 w-2 rounded-full ${u.is_online ? 'bg-emerald-400' : 'bg-slate-300'}`} />
              <span className="text-xs text-slate-500">{u.is_online ? 'Online' : 'Offline'}</span>
            </div>
            <div className="flex gap-1">
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`} title="Modifica"><Edit2 size={13} /></Button>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-amber-500 hover:text-amber-700" onClick={() => openResetPassword(u)} data-testid={`reset-pwd-${u.id}`} title="Reset Password"><KeyRound size={13} /></Button>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400 hover:text-red-600" onClick={() => handleDelete(u.id)} data-testid={`delete-user-${u.id}`} title="Elimina"><Trash2 size={13} /></Button>
            </div>
          </div>
          );
        })}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editUser ? 'Modifica Utente' : 'Nuovo Utente'}</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Nome</Label><Input value={form.nome} onChange={(e) => setForm(p => ({...p, nome: e.target.value}))} placeholder="Nome" data-testid="user-nome" /></div>
              <div><Label className="text-xs">Cognome</Label><Input value={form.cognome} onChange={(e) => setForm(p => ({...p, cognome: e.target.value}))} placeholder="Cognome" data-testid="user-cognome" /></div>
            </div>
            <div><Label className="text-xs">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm(p => ({...p, email: e.target.value}))} placeholder="email@esempio.com" data-testid="user-email" /></div>
            <div><Label className="text-xs">{editUser ? 'Nuova Password (lascia vuoto per non cambiare)' : 'Password'}</Label><Input type="password" value={form.password} onChange={(e) => setForm(p => ({...p, password: e.target.value}))} placeholder="Password" data-testid="user-password" /></div>
            <div>
              <Label className="text-xs">Ruolo</Label>
              <Select value={form.ruolo} onValueChange={(v) => setForm(p => ({...p, ruolo: v}))} data-testid="user-ruolo-wrapper">
                <SelectTrigger data-testid="user-ruolo"><SelectValue placeholder="Seleziona ruolo" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="super_admin">Admin</SelectItem>
                  <SelectItem value="dipendente">Dipendente</SelectItem>
                  <SelectItem value="collaboratore">Collaboratore (Sub-agente)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Interno VoIP (MyCentralino)</Label>
              <Select value={form.interno_voip || 'none'} onValueChange={(v) => setForm(p => ({...p, interno_voip: v === 'none' ? '' : v}))} data-testid="user-voip-wrapper">
                <SelectTrigger data-testid="user-voip"><SelectValue placeholder="Nessun interno" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessun interno</SelectItem>
                  {extensions.map(ext => (
                    <SelectItem key={ext.internal_number} value={ext.internal_number}>
                      {ext.internal_number} — {ext.description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleSave} disabled={!form.nome || !form.cognome || !form.email || (!editUser && !form.password)} className="w-full mt-2" data-testid="save-user-btn">
              {editUser ? 'Aggiorna' : 'Crea Utente'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Reset Password</DialogTitle></DialogHeader>
          {resetUser && (
            <div className="space-y-4 pt-2">
              <p className="text-sm text-slate-600">
                Stai resettando la password per <span className="font-semibold text-slate-900">{resetUser.nome} {resetUser.cognome}</span>
              </p>
              <div>
                <Label className="text-xs">Nuova Password</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Inserisci nuova password"
                  data-testid="reset-password-input"
                />
              </div>
              <Button onClick={handleResetPassword} className="w-full" data-testid="confirm-reset-pwd-btn">
                <KeyRound size={14} className="mr-2" /> Conferma Reset
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
